import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { utils } from '../utils/api';
import StorageQuotaModal from './StorageQuotaModal';
import { Upload, FileText, Folder, Sparkles, CheckCircle2, X } from 'lucide-react';

function DropZone({ onFileSelect }) {
  const [isDragOver, setIsDragOver] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [expiryDays, setExpiryDays] = useState(1); // Default to 24 hours (1 day) for free
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showStorageQuotaModal, setShowStorageQuotaModal] = useState(false);
  const [storageQuotaDetails, setStorageQuotaDetails] = useState(null);
  const [pendingExpiry, setPendingExpiry] = useState(null);
  const [sizeError, setSizeError] = useState(null);
  const [isUserPremium, setIsUserPremium] = useState(false);
  const [userTierLimits, setUserTierLimits] = useState({
    tier: 'free',
    maxFileSize: 10 * 1024 * 1024 * 1024,
    storageQuota: 10 * 1024 * 1024 * 1024,
    name: 'Free'
  });
  
  const fileInputRef = useRef(null);
  const folderInputRef = useRef(null);
  const uploadButtonsRef = useRef(null);
  const isOpeningPickerRef = useRef(false);
  // eslint-disable-next-line no-unused-vars
  const [folderMode, setFolderMode] = useState(false);

  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();

  // Check if current user is premium and load tier limits
  useEffect(() => {
    const checkPremium = async () => {
      try {
        const userId = localStorage.getItem('fileshare_user_id');
        if (!userId) return;
        const apiBase = '';
        const res = await fetch(`${apiBase}/api/user/storage?userId=${encodeURIComponent(userId)}`);
        if (res.ok) {
          const data = await res.json();
          const tier = data?.subscriptionTier?.tier || 'free';
          const isPaid = Boolean(tier && tier !== 'free' && data?.subscriptionTier?.status === 'active');
          if (isPaid) {
            setIsUserPremium(true);
            setExpiryDays(-1); // Default to Forever for active subscribers
            setUserTierLimits({
              tier,
              maxFileSize: data?.subscriptionTier?.maxFileSize || (tier.includes('premium') ? 1099511627776 : tier.includes('pro') ? 536870912000 : 107374182400),
              storageQuota: data?.totalStorageQuota || data?.subscriptionTier?.storageQuota || 107374182400,
              name: data?.subscriptionTier?.name || tier.toUpperCase()
            });
          } else {
            setIsUserPremium(false);
            setExpiryDays(1); // Free users strictly 24 hours
            setUserTierLimits({
              tier: 'free',
              maxFileSize: 10 * 1024 * 1024 * 1024,
              storageQuota: 10 * 1024 * 1024 * 1024,
              name: 'Free'
            });
          }
        }
      } catch (_) {}
    };
    checkPremium();

    const handleStorageExceeded = (e) => {
      setStorageQuotaDetails(e.detail);
      setShowStorageQuotaModal(true);
    };
    window.addEventListener('storage-quota-exceeded', handleStorageExceeded);
    return () => window.removeEventListener('storage-quota-exceeded', handleStorageExceeded);
  }, [user, isAuthenticated]);

  const openFilePicker = () => {
    if (isOpeningPickerRef.current) return;
    isOpeningPickerRef.current = true;
    if (fileInputRef.current) {
      setFolderMode(false);
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
    setTimeout(() => { isOpeningPickerRef.current = false; }, 300);
  };

  const openFolderPicker = () => {
    if (isOpeningPickerRef.current) return;
    isOpeningPickerRef.current = true;
    if (folderInputRef.current) {
      setFolderMode(true);
      folderInputRef.current.value = '';
      folderInputRef.current.click();
    }
    setTimeout(() => { isOpeningPickerRef.current = false; }, 300);
  };

  // Expiry options: 24 Hours (Free), 3 Days (Premium), 7 Days (Premium), 15 Days (Premium), 30 Days (Premium), Forever (Premium)
  const getExpiryDaysOptions = () => {
    return [
      { value: 1, label: '24 Hours — Free', isPremium: false },
      { value: 3, label: '3 Days — Premium', isPremium: true },
      { value: 7, label: '7 Days — Premium', isPremium: true },
      { value: 15, label: '15 Days — Premium', isPremium: true },
      { value: 30, label: '30 Days — Premium', isPremium: true },
      { value: -1, label: 'While Subscribed — Premium', isPremium: true }
    ];
  };

  const handleExpiryChange = (val) => {
    const selectedOpt = getExpiryDaysOptions().find(opt => opt.value === val);
    if (!selectedOpt) return;

    if (selectedOpt.isPremium && !isUserPremium) {
      setExpiryDays(1);
      setPendingExpiry(val);
      setShowUpgradeModal(true);
      return;
    }

    setPendingExpiry(null);
    setExpiryDays(val);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileSelection(files);
    }
  };

  const FREE_MAX_BYTES = 10 * 1024 * 1024 * 1024; // 10 GB

  const formatLimitLabel = (bytes) => {
    const gb = Math.round(bytes / (1024 * 1024 * 1024));
    return gb >= 1000 ? `${(gb / 1024).toFixed(0)} TB` : `${gb} GB`;
  };

  const handleFileSelection = (files) => {
    setSizeError(null);
    const validFiles = [];
    let totalSize = 0;

    for (const file of files) {
      validFiles.push(file);
      totalSize += file.size;
    }

    const activeMaxBytes = isUserPremium 
      ? (userTierLimits.maxFileSize || 100 * 1024 * 1024 * 1024) 
      : FREE_MAX_BYTES;

    if (!isUserPremium && totalSize > FREE_MAX_BYTES) {
      setSizeError('Total file size exceeds 10GB Free limit. Upgrade to a continuous paid plan for up to 1TB file uploads.');
    } else if (isUserPremium && totalSize > activeMaxBytes) {
      setSizeError(`Total file size exceeds your ${userTierLimits.name} plan limit of ${formatLimitLabel(activeMaxBytes)}. Upgrade your plan for higher transfer limits.`);
    }

    if (validFiles.length > 0) {
      setSelectedFiles(validFiles);
    }
  };

  const handleUpload = () => {
    if (selectedFiles.length === 0 || !onFileSelect) return;

    const totalSize = getTotalSize();
    const activeMaxBytes = isUserPremium 
      ? (userTierLimits.maxFileSize || 100 * 1024 * 1024 * 1024) 
      : FREE_MAX_BYTES;

    if (!isUserPremium && totalSize > FREE_MAX_BYTES) {
      setShowUpgradeModal(true);
      return;
    }
    if (isUserPremium && totalSize > activeMaxBytes) {
      setSizeError(`Total file size exceeds your plan limit of ${formatLimitLabel(activeMaxBytes)}.`);
      return;
    }

    const selectedDays = isUserPremium ? expiryDays : 1;
    onFileSelect(selectedFiles, selectedDays);
    setSelectedFiles([]);
    setSizeError(null);
  };

  const handleCancel = () => {
    setSelectedFiles([]);
    setSizeError(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };


  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const getTotalSize = () => {
    return selectedFiles.reduce((total, file) => total + file.size, 0);
  };

  // IDE-style file icon
  const FileIcon = () => (
    <FileText size={16} color="#9ca3af" />
  );

  const UploadIcon = () => (
    <div className="home-upload-icon-tile" style={{
      width: '54px',
      height: '54px',
      borderRadius: '14px',
      backgroundColor: '#181a22',
      border: `1px solid ${isDragOver ? '#ffffff' : '#282d3b'}`,
      boxShadow: isDragOver ? '0 0 24px rgba(255, 255, 255, 0.12)' : '0 4px 16px rgba(0, 0, 0, 0.35)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.2s cubic-bezier(0.16, 1, 0.3, 1)',
      transform: isDragOver ? 'scale(1.08)' : 'scale(1)'
    }}>
      <Upload className="home-upload-icon" size={24} strokeWidth={1.8} color={isDragOver ? '#ffffff' : '#e4e4e7'} />
    </div>
  );

  return (
    <div className="w-full">
      {window.__bandwidthBlocked?.active ? (
        <div style={{
          border: '1px solid #dc2626',
          borderRadius: '6px',
          backgroundColor: '#12141a',
          padding: '24px',
          color: '#ffffff',
          textAlign: 'center'
        }}>
          <h3 style={{ margin: 0, marginBottom: '8px', color: '#ffffff' }}>Bandwidth limit reached</h3>
          <p style={{ margin: 0, marginBottom: '12px', color: '#9ca3af' }}>Upgrade your plan to continue uploading.</p>
          <button className="home-action-primary" onClick={() => (window.location.href = '/pricing')} style={{
            backgroundColor: '#ffffff', color: '#0a0b0d', padding: '8px 18px', border: 'none', borderRadius: '6px', fontWeight: '600', cursor: 'pointer'
          }}>Upgrade Plan</button>
        </div>
      ) : window.__storageBlocked?.active ? (
        <div style={{
          border: '1px solid #1f232e',
          borderRadius: '6px',
          backgroundColor: '#12141a',
          padding: '28px 20px',
          color: '#ffffff',
          textAlign: 'center'
        }}>
          <div style={{
            width: '42px',
            height: '42px',
            borderRadius: '8px',
            backgroundColor: 'rgba(255, 255, 255, 0.06)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 12px auto'
          }}>
            <Sparkles size={20} color="#ffffff" />
          </div>
          <h3 style={{ margin: '0 0 6px 0', fontSize: '16px', fontWeight: '600', color: '#ffffff' }}>
            Free Storage Limit Reached (10 GB)
          </h3>
          <p style={{ margin: '0 0 16px 0', fontSize: '13px', color: '#9ca3af', lineHeight: '1.45', maxWidth: '420px', marginLeft: 'auto', marginRight: 'auto' }}>
            You have used your 10 GB free active storage. Upgrade to Basic ($5/mo) for 100 GB storage and permanent links, or manage existing files to free up space.
          </p>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <button
              type="button"
              className="home-action-primary"
              onClick={() => setShowStorageQuotaModal(true)}
              style={{
                backgroundColor: '#ffffff',
                color: '#0a0b0d',
                padding: '9px 18px',
                border: 'none',
                borderRadius: '6px',
                fontWeight: '600',
                fontSize: '13px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'background-color 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#e4e4e7'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ffffff'}
            >
              <Sparkles size={15} />
              <span>Upgrade Plan ($5/mo)</span>
            </button>
            <button
              type="button"
              className="home-action-secondary"
              onClick={() => navigate('/my-files')}
              style={{
                backgroundColor: '#1b1e27',
                color: '#e4e4e7',
                padding: '9px 16px',
                border: '1px solid #282d3b',
                borderRadius: '6px',
                fontWeight: '500',
                fontSize: '13px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#374151';
                e.currentTarget.style.backgroundColor = '#222632';
                e.currentTarget.style.color = '#ffffff';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#282d3b';
                e.currentTarget.style.backgroundColor = '#1b1e27';
                e.currentTarget.style.color = '#e4e4e7';
              }}
            >
              <Folder size={15} />
              <span>Manage My Files</span>
            </button>
          </div>
        </div>
      ) : selectedFiles.length === 0 ? (
        <div
          style={{
            border: `1.5px dashed ${isDragOver ? '#ffffff' : '#282d3b'}`,
            borderRadius: '6px',
            padding: '48px 24px',
            textAlign: 'center',
            cursor: 'pointer',
            transition: 'all 0.2s ease',
            backgroundColor: isDragOver ? 'rgba(255, 255, 255, 0.04)' : '#12141a'
          }}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={(e) => {
            // Only trigger if clicking on the dropzone area itself, not on buttons
            if (e.target === e.currentTarget || 
                e.target.tagName === 'H3' || 
                e.target.tagName === 'P' || 
                e.target.tagName === 'DIV' ||
                e.target.tagName === 'svg' ||
                e.target.tagName === 'path') {
              if (!window.__bandwidthBlocked?.active && !window.__storageBlocked?.active) {
                openFilePicker();
              }
            }
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div style={{ marginBottom: '16px' }}>
              <UploadIcon />
            </div>
            
            <h3 style={{ 
              fontSize: '16px', 
              fontWeight: '600', 
              color: '#ffffff', 
              marginBottom: '8px',
              margin: '0 0 8px 0'
            }}>
              {isDragOver ? 'Drop your files here' : 'Upload files'}
            </h3>

            <p style={{
              fontSize: '12px',
              color: '#9ca3af',
              marginBottom: '24px',
              margin: '0 0 24px 0',
              lineHeight: '1.4'
            }}>
              Drag and drop multiple files or a folder here, or click to browse
            </p>
            
            <div ref={uploadButtonsRef} className="upload-buttons" style={{ display: 'flex', gap: '12px' }}>
              <button
                type="button"
                className="home-action-secondary"
                disabled={!!window.__bandwidthBlocked?.active || !!window.__storageBlocked?.active}
                onMouseDown={(e) => {
                  e.stopPropagation();
                  if (e.nativeEvent && typeof e.nativeEvent.stopImmediatePropagation === 'function') {
                    e.nativeEvent.stopImmediatePropagation();
                  }
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!window.__bandwidthBlocked?.active && !window.__storageBlocked?.active) openFolderPicker();
                }}
                style={{
                  backgroundColor: '#1b1e27',
                  color: '#e4e4e7',
                  border: '1px solid #282d3b',
                  padding: '9px 18px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#222632';
                  e.target.style.borderColor = '#374151';
                  e.target.style.color = '#ffffff';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#1b1e27';
                  e.target.style.borderColor = '#282d3b';
                  e.target.style.color = '#e4e4e7';
                }}
              >
                Choose Folder
              </button>
              <button
                type="button"
                className="home-action-primary"
                disabled={!!window.__bandwidthBlocked?.active || !!window.__storageBlocked?.active}
                onMouseDown={(e) => {
                  e.stopPropagation();
                  if (e.nativeEvent && typeof e.nativeEvent.stopImmediatePropagation === 'function') {
                    e.nativeEvent.stopImmediatePropagation();
                  }
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (!window.__bandwidthBlocked?.active && !window.__storageBlocked?.active) openFilePicker();
                }}
                style={{
                  backgroundColor: '#ffffff',
                  color: '#0a0b0d',
                  padding: '9px 18px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  fontWeight: '600',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'background-color 0.15s ease'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#e4e4e7'}
                onMouseLeave={(e) => e.target.style.backgroundColor = '#ffffff'}
              >
                Choose Files
              </button>
            </div>

            {/* Value Prop Banner */}
            <div className="home-upload-perks" style={{
              marginTop: '18px', 
              fontSize: '11px', 
              color: '#94a3b8', 
              fontWeight: '400',
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center', 
              gap: '8px', 
              flexWrap: 'wrap' 
            }}>
              <span>Free: Up to 10GB per file</span>
              <span>•</span>
              <span>Unlimited downloads & $0 fees</span>
              <span>•</span>
              <Link 
                to="/pricing" 
                style={{ 
                  color: '#94a3b8', 
                  textDecoration: 'underline', 
                  fontWeight: '400',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '3px'
                }}
                onMouseEnter={(e) => e.target.style.color = '#e2e8f0'}
                onMouseLeave={(e) => e.target.style.color = '#94a3b8'}
              >
                Need to share up to 1TB? Upgrade to Premium →
              </Link>
            </div>
          </div>

          {/* Dedicated inputs: one for files, one for folders (static attributes) */}
          <input
            ref={fileInputRef}
            type="file"
            multiple
            style={{ display: 'none' }}
            onChange={(e) => {
              console.log(`📄 HOME(files): ${e.target.files?.length || 0} selected`);
              if (e.target.files && e.target.files.length > 0) {
                const files = Array.from(e.target.files);
                setFolderMode(false);
                handleFileSelection(files);
                e.target.value = '';
              }
            }}
          />
          <input
            ref={folderInputRef}
            type="file"
            webkitdirectory=""
            directory=""
            mozdirectory=""
            style={{ display: 'none' }}
            onChange={(e) => {
              console.log(`📁 HOME(folder): ${e.target.files?.length || 0} items`);
              if (e.target.files && e.target.files.length > 0) {
                const files = Array.from(e.target.files);
                setFolderMode(true);
                if (files[0].webkitRelativePath) {
                  console.log('📁 HOME: Folder detected:', files[0].webkitRelativePath);
                }
                handleFileSelection(files);
                e.target.value = '';
              }
            }}
          />
        </div>
      ) : (
        <div style={{
          border: '1px solid #1f232e',
          borderRadius: '8px',
          backgroundColor: '#12141a',
          padding: '24px'
        }}>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            marginBottom: '16px'
          }}>
            <h3 style={{ 
              fontSize: '14px', 
              fontWeight: '600', 
              color: '#ffffff',
              margin: '0'
            }}>
              Files to Upload ({selectedFiles.length})
            </h3>
            <span style={{ 
              fontSize: '12px', 
              color: '#9ca3af'
            }}>
              Total: {utils.formatFileSize(getTotalSize())}
            </span>
          </div>

          <div style={{
            maxHeight: '300px',
            overflowY: 'auto',
            marginBottom: '16px',
            border: '1px solid #242834',
            borderRadius: '6px',
            backgroundColor: '#181a22'
          }}>
            {selectedFiles.map((file, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '9px 12px',
                  borderBottom: index < selectedFiles.length - 1 ? '1px solid #242834' : 'none',
                  fontSize: '12px'
                }}
              >
                <div style={{ color: '#9ca3af', marginRight: '10px', display: 'flex', alignItems: 'center' }}>
                  <FileIcon />
                </div>
                <div style={{ flex: '1', minWidth: '0' }}>
                  <div style={{ 
                    color: '#ffffff', 
                    fontWeight: '400',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}>
                    {file.name}
                  </div>
                  <div style={{ color: '#71717a', fontSize: '11px', marginTop: '1px' }}>
                    {utils.formatFileSize(file.size)}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => removeFile(index)}
                  style={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: '#ef4444',
                    cursor: 'pointer',
                    padding: '4px',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginLeft: '8px'
                  }}
                  title="Remove file"
                >
                  <X size={15} />
                </button>
              </div>
            ))}
          </div>

          {sizeError && (
            <div style={{
              padding: '10px 14px',
              backgroundColor: '#2d1b1b',
              border: '1px solid #4e2a2a',
              borderRadius: '6px',
              color: '#f87171',
              fontSize: '12px',
              marginBottom: '14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '8px'
            }}>
              <span>{sizeError}</span>
              <button
                type="button"
                className="home-action-primary"
                onClick={() => navigate('/pricing')}
                style={{
                  backgroundColor: '#ffffff',
                  color: '#0a0b0d',
                  border: 'none',
                  borderRadius: '4px',
                  padding: '4px 10px',
                  fontSize: '11px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  whiteSpace: 'nowrap'
                }}
              >
                Upgrade
              </button>
            </div>
          )}

          {/* File Expiry Selection */}
          <div style={{
            padding: '14px 16px',
            backgroundColor: '#181a22',
            border: '1px solid #242834',
            borderRadius: '6px',
            marginBottom: '16px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" style={{ color: '#9ca3af' }}>
                  <path d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M16.2,16.2L11,13V7H12.5V12.2L17,14.4L16.2,16.2Z"/>
                </svg>
                <span style={{ fontSize: '12px', color: '#9ca3af', fontWeight: '500' }}>
                  File Validity:
                </span>
                <select
                  aria-label="Choose how long files stay available"
                  value={expiryDays}
                  onChange={(e) => handleExpiryChange(parseInt(e.target.value, 10))}
                  style={{
                    backgroundColor: '#12141a',
                    color: '#ffffff',
                    border: '1px solid #282d3b',
                    borderRadius: '4px',
                    padding: '5px 10px',
                    fontSize: '12px',
                    cursor: 'pointer',
                    outline: 'none',
                    fontWeight: '500'
                  }}
                >
                  {getExpiryDaysOptions().map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                {isUserPremium && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '5px',
                    backgroundColor: 'rgba(255, 255, 255, 0.08)',
                    border: '1px solid rgba(255, 255, 255, 0.15)',
                    color: '#ffffff',
                    padding: '4px 10px',
                    borderRadius: '4px',
                    fontSize: '11px',
                    fontWeight: '500'
                  }}>
                    <Sparkles size={13} color="#ffffff" />
                    <span>Premium Active</span>
                  </div>
                )}
              </div>
            </div>
            <p style={{ fontSize: '11px', color: '#71717a', margin: '8px 0 0 0' }}>
              {isUserPremium
                ? 'Files stay active as long as your subscription is active, or choose 3, 7, 15, or 30 days.'
                : 'Free transfers expire after 24 hours. Choose a longer period to see Premium plan options.'}
            </p>
          </div>

          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              className="home-action-secondary"
              onClick={handleCancel}
              style={{
                backgroundColor: '#1b1e27',
                color: '#e4e4e7',
                border: '1px solid #282d3b',
                padding: '9px 18px',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'all 0.15s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#222632';
                e.currentTarget.style.borderColor = '#374151';
                e.currentTarget.style.color = '#ffffff';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#1b1e27';
                e.currentTarget.style.borderColor = '#282d3b';
                e.currentTarget.style.color = '#e4e4e7';
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="home-action-primary"
              onClick={handleUpload}
              style={{
                backgroundColor: '#ffffff',
                color: '#0a0b0d',
                padding: '9px 20px',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: '600',
                border: 'none',
                cursor: 'pointer',
                transition: 'background-color 0.15s ease'
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#e4e4e7'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#ffffff'}
            >
              Upload Files
            </button>
          </div>
        </div>
      )}

      {/* Upgrade Modal for Premium Expiry / File Size */}
      {showUpgradeModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000,
          backdropFilter: 'blur(8px)',
          padding: '16px',
          fontFamily: "'Largetransfer', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
        }}>
          <div style={{
            backgroundColor: '#12141a',
            border: '1px solid #1f232e',
            borderRadius: '8px',
            padding: '28px',
            maxWidth: '440px',
            width: '100%',
            boxShadow: '0 20px 50px rgba(0, 0, 0, 0.8)'
          }}>
            <div style={{ textAlign: 'center', marginBottom: '20px' }}>
              <div style={{
                width: '42px',
                height: '42px',
                borderRadius: '8px',
                backgroundColor: '#181a22',
                border: '1px solid #242834',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 12px auto'
              }}>
                <Sparkles size={20} color="#ffffff" />
              </div>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#ffffff', margin: '0 0 8px 0', letterSpacing: '-0.015em' }}>
                {pendingExpiry !== null ? 'Premium Required' : 'Unlock Extended File Validity'}
              </h3>
              <p style={{ fontSize: '13px', color: '#9ca3af', margin: 0, lineHeight: '1.5', fontWeight: '400' }}>
                {pendingExpiry !== null
                  ? `A paid plan is required to keep files ${pendingExpiry === -1 ? 'active while subscribed' : `for ${pendingExpiry} days`}. Free transfers expire after 24 hours.`
                  : 'Upgrade for extended file validity, larger uploads, and more storage.'}
              </p>
            </div>

            <div style={{
              backgroundColor: '#181a22',
              borderRadius: '6px',
              padding: '16px',
              marginBottom: '20px',
              border: '1px solid #242834',
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
              fontSize: '12px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#e4e4e7', fontWeight: '400' }}>
                <CheckCircle2 size={16} color="#ffffff" />
                <span>Up to 100 GB upload file size</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#e4e4e7', fontWeight: '400' }}>
                <CheckCircle2 size={16} color="#ffffff" />
                <span>100 GB to 1 TB cloud storage quota</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#e4e4e7', fontWeight: '400' }}>
                <CheckCircle2 size={16} color="#ffffff" />
                <span>Unlimited monthly transfer bandwidth</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#e4e4e7', fontWeight: '400' }}>
                <CheckCircle2 size={16} color="#ffffff" />
                <span>Active while subscribed, with 3, 7, 15, or 30-day options</span>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <button
                type="button"
                className="home-action-primary"
                onClick={() => {
                  setShowUpgradeModal(false);
                  setPendingExpiry(null);
                  navigate('/pricing');
                }}
                style={{
                  backgroundColor: '#ffffff',
                  color: '#0a0b0d',
                  padding: '11px 18px',
                  borderRadius: '6px',
                  border: 'none',
                  fontSize: '13px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  transition: 'background-color 0.15s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#e4e4e7';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = '#ffffff';
                }}
              >
                <Sparkles size={16} />
                <span>View Premium Plans</span>
              </button>

              <button
                type="button"
                onClick={() => {
                  setShowUpgradeModal(false);
                  setPendingExpiry(null);
                  setExpiryDays(1);
                }}
                style={{
                  backgroundColor: 'transparent',
                  color: '#71717a',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: 'none',
                  fontSize: '12px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'color 0.15s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = '#ffffff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = '#71717a';
                }}
              >
                Continue with 24 Hours (Free)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Free Storage Quota Modal (Benefit-driven) */}
      <StorageQuotaModal
        isOpen={showStorageQuotaModal}
        onClose={() => setShowStorageQuotaModal(false)}
        quotaDetails={storageQuotaDetails || window.__storageBlocked?.details}
      />
    </div>
  );
}

export default DropZone;
